import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ChevronDown, Building2, Stethoscope, UtensilsCrossed, Home, Scissors, Wrench, GraduationCap, ShoppingBag, Flower2, Dumbbell, Sparkles, Star, Zap, Send, Phone, Mail, User, MessageSquare } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_post-planner-51/artifacts/17buinbo_AFA%20transparent.png';

const INDUSTRIES = [
  { id: 'landscaping', label: 'Landscaping & Lawn Care', icon: Flower2, color: '#7ED321',
    examples: ['Seasonal lawn care tips', 'Before & after project showcases', 'Holiday yard prep reminders', 'Plant care spotlights'],
    libraryNote: 'We curate professional landscape, garden, and outdoor living imagery matched to your services and local seasons.' },
  { id: 'dental', label: 'Dental & Medical', icon: Stethoscope, color: '#01939E',
    examples: ['Oral health awareness posts', 'Meet the team spotlights', 'Holiday smile reminders', 'Office culture moments'],
    libraryNote: 'We source professional healthcare imagery, team-friendly stock photos, and seasonal wellness visuals for your practice.' },
  { id: 'restaurant', label: 'Restaurants & Food Service', icon: UtensilsCrossed, color: '#01939E',
    examples: ['Dish & menu spotlights', 'Behind the kitchen posts', 'Holiday dining reminders', 'Local ingredient features'],
    libraryNote: 'We curate appetizing food photography, warm hospitality imagery, and seasonal dining visuals for your brand.' },
  { id: 'realestate', label: 'Real Estate', icon: Home, color: '#01939E',
    examples: ['Homeowner tips & seasonal maintenance', 'Neighborhood spotlights', 'Market confidence posts', 'Holiday home prep'],
    libraryNote: 'We source professional property, neighborhood, and lifestyle imagery that keeps your brand visible between listings.' },
  { id: 'salon', label: 'Salons & Spas', icon: Scissors, color: '#7ED321',
    examples: ['Style trend spotlights', 'Self-care reminders', 'Holiday glam posts', 'Meet the stylist features'],
    libraryNote: 'We curate beauty, wellness, and lifestyle imagery that reflects your salon\'s vibe and keeps clients thinking of you.' },
  { id: 'trades', label: 'Home Services & Trades', icon: Wrench, color: '#01939E',
    examples: ['Seasonal home maintenance tips', 'Before & after project showcases', 'Safety reminders', 'Holiday prep checklists'],
    libraryNote: 'We source professional trade, home improvement, and seasonal maintenance imagery matched to your services.' },
  { id: 'education', label: 'Education & Tutoring', icon: GraduationCap, color: '#01939E',
    examples: ['Study tips & motivation posts', 'Academic milestone celebrations', 'Holiday learning activities', 'Back-to-school reminders'],
    libraryNote: 'We curate education-focused, inspiring imagery that keeps parents and students engaged with your brand.' },
  { id: 'retail', label: 'Retail & Small Business', icon: ShoppingBag, color: '#7ED321',
    examples: ['Product spotlights', 'Shop local reminders', 'Holiday gift guides', 'Behind the business moments'],
    libraryNote: 'We source product-friendly, lifestyle, and seasonal retail imagery that keeps your store top of mind.' },
  { id: 'fitness', label: 'Fitness & Wellness', icon: Dumbbell, color: '#01939E',
    examples: ['Workout motivation posts', 'Wellness tip spotlights', 'Holiday fitness reminders', 'Member success celebrations'],
    libraryNote: 'We curate energizing fitness, health, and lifestyle imagery that keeps your community motivated.' },
  { id: 'other', label: 'Other Industry', icon: Building2, color: '#A2D0D5',
    examples: ['Industry-relevant tips & insights', 'Team & culture spotlights', 'Holiday acknowledgments', 'Community engagement posts'],
    libraryNote: 'We curate professional stock imagery matched specifically to your industry, services, and brand identity.' },
];

function ContactModal({ isOpen, onClose, tier, industry }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', business: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const tierLabel = tier === 'beta' ? 'Beta Tester (Free)' : tier === 'annual' ? 'Annual Plan ($3,999/yr)' : 'Monthly Plan ($249/mo)';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tier, industry: industry || '' }),
      });
    } catch (err) {
      // Still show success
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-[#003C59]/90 backdrop-blur-md" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="contact-modal"
      >
        {/* Modal header */}
        <div className="bg-[#003C59] px-6 py-5">
          <div className="flex items-center gap-3 mb-2">
            <img src={LOGO_URL} alt="Anne Flinn Advertising" className="w-10 h-10 rounded-lg object-contain bg-white/10 p-0.5" />
            <div>
              <h3 className="text-white font-bold text-lg">Connect with Anne</h3>
              <p className="text-[#A2D0D5] text-sm">Let's get your Facebook working for you</p>
            </div>
          </div>
          <div className="mt-2 inline-block px-3 py-1 rounded-full text-xs font-semibold"
               style={{ backgroundColor: tier === 'beta' ? '#7ED321' : tier === 'annual' ? '#01939E' : '#A2D0D5',
                        color: tier === 'beta' ? '#003C59' : '#fff' }}>
            {tierLabel}
          </div>
        </div>

        {submitted ? (
          <div className="px-6 py-10 text-center">
            <div className="w-14 h-14 rounded-full bg-[#7ED321]/15 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-[#7ED321]" />
            </div>
            <h4 className="text-xl font-bold text-[#003C59] mb-2">Thank you!</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              Anne will be in touch shortly. She personally reviews every inquiry, 
              you're not getting a bot or an assistant.
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-2.5 bg-[#003C59] text-white rounded-lg font-medium hover:bg-[#003C59]/90 transition-colors text-sm"
              data-testid="modal-close-btn"
            >
              Got it
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {industry && (
              <p className="text-xs text-[#01939E] font-medium bg-[#01939E]/5 px-3 py-1.5 rounded-lg">
                Industry: {INDUSTRIES.find(i => i.id === industry)?.label || 'Other'}
              </p>
            )}
            <div>
              <label className="block text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1.5">Your Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A2D0D5]" />
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#01939E]/30 focus:border-[#01939E]"
                  placeholder="Jane Smith"
                  data-testid="contact-name"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1.5">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A2D0D5]" />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#01939E]/30 focus:border-[#01939E]"
                  placeholder="jane@yourbusiness.com"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1.5">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A2D0D5]" />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#01939E]/30 focus:border-[#01939E]"
                    placeholder="(555) 123-4567"
                    data-testid="contact-phone"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1.5">Business Name</label>
                <input
                  type="text"
                  value={form.business}
                  onChange={(e) => setForm({ ...form, business: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#01939E]/30 focus:border-[#01939E]"
                  placeholder="Your Business"
                  data-testid="contact-business"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1.5">Anything you'd like Anne to know?</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-[#A2D0D5]" />
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#01939E]/30 focus:border-[#01939E] resize-none"
                  placeholder="Tell me about your business, your goals, or just say hi..."
                  data-testid="contact-message"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-[#01939E] hover:bg-[#017a83] text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-lg shadow-[#01939E]/20 disabled:opacity-60"
              data-testid="contact-submit-btn"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Sending...' : 'Send to Anne'}
            </button>
            <p className="text-[10px] text-gray-400 text-center">
              Anne personally reads every message. Expect a response within 24 hours.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default function PricingPage() {
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTier, setModalTier] = useState('monthly');

  const industry = INDUSTRIES.find(i => i.id === selectedIndustry);

  const openContact = (tier) => {
    setModalTier(tier);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#fafbfc]">
      {/* Sales Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Anne Flinn Advertising" className="h-10 w-auto" />
            <span className="text-xs text-[#003C59]/50 font-medium italic hidden sm:inline">Developed for the small business, by Anne.</span>
          </Link>
          <div className="flex items-center gap-5">
            <Link
              to="/how-it-works"
              className="text-sm font-medium text-[#003C59]/60 hover:text-[#01939E] transition-colors"
              data-testid="pricing-nav-hiw"
            >
              How It Works
            </Link>
            <span
              className="text-sm font-semibold text-[#003C59] border-b-2 border-[#01939E] pb-0.5"
              data-testid="pricing-nav-active"
            >
              Pricing
            </span>
          </div>
        </div>
      </nav>

      {/* Compact Hero */}
      <div className="bg-[#003C59] text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Anne Flinn Advertising" className="w-12 h-12 drop-shadow-lg" data-testid="hero-logo" />
            <div>
              <h1 className="text-lg md:text-xl font-bold tracking-tight">Done-for-You Facebook Posting</h1>
              <p className="text-[#A2D0D5] text-sm">12 professional posts/month, curated and published for you.</p>
            </div>
          </div>
          <p className="text-xs text-[#7ED321] font-medium tracking-wide uppercase whitespace-nowrap">Anne Flinn Advertising</p>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-2 flex justify-end">
          <a href="/how-it-works" className="text-xs text-[#A2D0D5] hover:text-white transition-colors">How It Works &rarr;</a>
        </div>
      </div>

      {/* Industry Selector — compact inline */}
      <div className="max-w-5xl mx-auto px-6 mt-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <p className="text-xs font-semibold text-[#003C59] uppercase tracking-wide shrink-0">Your Industry:</p>
          
          {/* Mobile dropdown */}
          <div className="sm:hidden relative w-full">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-left bg-white text-sm"
              data-testid="industry-dropdown-btn"
            >
              <span className="text-gray-700">
                {industry ? industry.label : 'Choose your industry...'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                {INDUSTRIES.map(ind => (
                  <button
                    key={ind.id}
                    onClick={() => { setSelectedIndustry(ind.id); setDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${selectedIndustry === ind.id ? 'bg-[#01939E]/5 text-[#01939E]' : 'text-gray-700'}`}
                  >
                    <ind.icon className="w-4 h-4" style={{ color: ind.color }} />
                    {ind.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop inline pills */}
          <div className="hidden sm:flex flex-wrap gap-1.5">
            {INDUSTRIES.map(ind => {
              const isActive = selectedIndustry === ind.id;
              return (
                <button
                  key={ind.id}
                  onClick={() => setSelectedIndustry(ind.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    isActive
                      ? 'border-[#01939E] bg-[#01939E]/10 text-[#003C59]'
                      : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`industry-${ind.id}`}
                >
                  <ind.icon className="w-3.5 h-3.5" style={{ color: isActive ? ind.color : '#94a3b8' }} />
                  {ind.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="max-w-5xl mx-auto px-6 mt-5 mb-16">
        <div className="grid md:grid-cols-3 gap-5">

          {/* Beta Tester */}
          <div className="bg-white rounded-xl border-2 border-[#7ED321] shadow-sm overflow-hidden relative" data-testid="tier-beta">
            <div className="absolute top-0 left-0 right-0 bg-[#7ED321] text-[#003C59] text-[11px] font-bold uppercase tracking-widest text-center py-1.5">
              Limited Spots
            </div>
            <div className="bg-[#003C59] px-6 pt-9 pb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#7ED321]" />
                <h3 className="text-white text-lg font-bold">Beta Tester</h3>
              </div>
              <p className="text-[#A2D0D5] text-sm mt-1">Help shape the service</p>
            </div>
            <div className="px-6 py-6">
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-[#7ED321]">Free</span>
              </div>
              <p className="text-xs text-gray-400 mb-1">No library fee &middot; No monthly fee</p>
              <p className="text-xs text-[#01939E] font-semibold mb-4">A $3,999 value</p>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />
                  Full 12-month posting service at zero cost
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />
                  Image library build included
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />
                  12 posts per month
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />
                  Monthly approval PDF
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />
                  Auto-published to Facebook
                </li>
              </ul>
              <div className="mt-5 p-3 rounded-lg bg-[#7ED321]/5 border border-[#7ED321]/20">
                <p className="text-xs text-[#003C59] font-medium">In exchange, Anne asks for:</p>
                <ul className="mt-1.5 space-y-1 text-sm text-[#003C59]/80">
                  <li className="flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-[#7ED321] shrink-0" />
                    Honest feedback on the process
                  </li>
                  <li className="flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-[#7ED321] shrink-0" />
                    A testimonial if you're happy
                  </li>
                  <li className="flex items-center gap-1.5">
                    <Star className="w-3 h-3 text-[#7ED321] shrink-0" />
                    A referral to another business
                  </li>
                </ul>
              </div>
              <button
                onClick={() => openContact('beta')}
                className="mt-5 block w-full text-center px-4 py-2.5 bg-[#7ED321] hover:bg-[#6fc01a] text-[#003C59] font-semibold rounded-lg transition-colors text-sm cursor-pointer"
                data-testid="beta-cta-btn"
              >
                Claim a Beta Spot
              </button>
            </div>
          </div>

          {/* Monthly */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden relative opacity-80 hidden md:block" data-testid="tier-monthly">
            <div className="absolute top-0 left-0 right-0 bg-gray-500 text-white text-[11px] font-bold uppercase tracking-widest text-center py-1.5">
              Coming January 2027
            </div>
            <div className="bg-[#003C59] px-6 pt-9 pb-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#01939E]" />
                <h3 className="text-white text-lg font-bold">Monthly</h3>
              </div>
              <p className="text-[#A2D0D5] text-sm mt-1">Flexible month-to-month</p>
            </div>
            <div className="px-6 py-6">
              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#003C59]">$249</span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">+ $1,500 one-time library build</p>
              </div>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  12 posts per month, every month
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  1–4 curated images per post
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  All major holidays acknowledged
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  Professionally written copy
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  Monthly approval PDF
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  Auto-published to Facebook
                </li>
              </ul>
              {industry && (
                <div className="mt-5 p-3 rounded-lg bg-[#01939E]/5 border border-[#01939E]/15">
                  <p className="text-xs text-[#01939E] font-medium mb-1.5">Sample posts for {industry.label}:</p>
                  <ul className="space-y-1">
                    {industry.examples.map((ex, i) => (
                      <li key={i} className="text-sm text-[#003C59]/80 flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-[#01939E] shrink-0"></span>
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {industry && (
                <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <p className="text-xs text-gray-500 font-medium mb-1">Library for {industry.label}:</p>
                  <p className="text-sm text-gray-600">{industry.libraryNote}</p>
                </div>
              )}
              <button
                disabled
                className="mt-5 block w-full text-center px-4 py-2.5 bg-gray-300 text-gray-500 font-semibold rounded-lg text-sm cursor-not-allowed"
                data-testid="monthly-cta-btn"
              >
                Coming January 2027
              </button>
            </div>
          </div>

          {/* Annual */}
          <div className="bg-white rounded-xl border-2 border-[#01939E] shadow-md overflow-hidden relative opacity-80 hidden md:block" data-testid="tier-annual">
            <div className="absolute top-0 left-0 right-0 bg-gray-500 text-white text-[11px] font-bold uppercase tracking-widest text-center py-1.5">
              Coming January 2027
            </div>
            <div className="bg-[#003C59] px-6 pt-9 pb-4">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#01939E]" />
                <h3 className="text-white text-lg font-bold">Annual</h3>
              </div>
              <p className="text-[#A2D0D5] text-sm mt-1">One payment. One year. Done.</p>
            </div>
            <div className="px-6 py-6">
              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#003C59]">$3,999</span>
                  <span className="text-gray-500 text-sm">/year</span>
                </div>
                <p className="text-xs text-[#01939E] font-semibold mt-1">Save $489, library included</p>
              </div>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  <span><strong>Image library build included</strong> (no extra fee)</span>
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  12 posts per month for 12 months
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  1–4 curated images per post
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  All major holidays acknowledged
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  Professionally written copy
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  Monthly approval PDF
                </li>
                <li className="flex gap-2.5">
                  <Check className="w-4 h-4 text-[#01939E] mt-0.5 shrink-0" />
                  Auto-published to Facebook
                </li>
              </ul>
              {industry && (
                <div className="mt-5 p-3 rounded-lg bg-[#01939E]/5 border border-[#01939E]/15">
                  <p className="text-xs text-[#01939E] font-medium mb-1.5">Sample posts for {industry.label}:</p>
                  <ul className="space-y-1">
                    {industry.examples.map((ex, i) => (
                      <li key={i} className="text-sm text-[#003C59]/80 flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-[#01939E] shrink-0"></span>
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                disabled
                className="mt-5 block w-full text-center px-4 py-2.5 bg-gray-300 text-gray-500 font-semibold rounded-lg text-sm cursor-not-allowed"
                data-testid="annual-cta-btn"
              >
                Coming January 2027
              </button>
            </div>
          </div>

        </div>

        {/* Monthly math breakdown */}
        <div className="mt-6 text-center hidden md:block">
          <p className="text-xs text-gray-400">
            Monthly total: $1,500 library + 12 months &times; $249 = $4,488 &nbsp;|&nbsp; Annual: $3,999 all-in &nbsp;|&nbsp; Beta: $0
          </p>
        </div>

        {/* This Is / This Is Not */}
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-bold text-[#003C59] mb-4 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#7ED321]/15 flex items-center justify-center">
                <Check className="w-3.5 h-3.5 text-[#7ED321]" />
              </div>
              This Service IS
            </h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex gap-2.5"><Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />Consistent, professional posts showing your business is open and active</li>
              <li className="flex gap-2.5"><Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />Curated images from your managed library, not generic clip art</li>
              <li className="flex gap-2.5"><Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />Holiday acknowledgments your customers expect to see</li>
              <li className="flex gap-2.5"><Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />One monthly approval: check the boxes, done</li>
              <li className="flex gap-2.5"><Check className="w-4 h-4 text-[#7ED321] mt-0.5 shrink-0" />A baseline presence: proof of life for your business</li>
            </ul>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-base font-bold text-[#003C59] mb-4 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-3.5 h-3.5 text-red-500" />
              </div>
              This Service IS NOT
            </h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex gap-2.5"><X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />Event promotion or time-sensitive announcements</li>
              <li className="flex gap-2.5"><X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />Reels, Stories, or video production</li>
              <li className="flex gap-2.5"><X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />Instagram, TikTok, or any platform beyond Facebook</li>
              <li className="flex gap-2.5"><X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />Custom photography or content shoots</li>
              <li className="flex gap-2.5"><X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />Social media advertising or paid promotion</li>
            </ul>
          </div>
        </div>

        {/* Personal Anne section */}
        <div className="mt-12 bg-[#003C59] rounded-2xl p-8 md:p-10 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]"
               style={{ backgroundImage: 'radial-gradient(circle at 90% 80%, #7ED321 0%, transparent 40%)' }} />
          <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-8">
            <img
              src={LOGO_URL}
              alt="Anne Flinn Advertising"
              className="w-24 h-24 rounded-xl object-contain bg-white/10 p-2 shrink-0"
              data-testid="anne-section-logo"
            />
            <div>
              <h3 className="text-xl md:text-2xl font-bold mb-3">
                Built by <span className="text-[#7ED321]">Anne Flinn</span>, not an algorithm.
              </h3>
              <p className="text-[#A2D0D5] leading-relaxed text-sm md:text-base">
                With decades of advertising industry knowledge, Anne built this service for one reason: 
                too many good businesses look closed on Facebook. She personally curates every library, 
                reviews every post calendar, and makes sure your brand looks exactly the way it should. 
                No bots. No outsourcing. Just Anne and her team, doing what they've done for decades: helping businesses advertise smarter.
              </p>
              <p className="text-white/60 text-xs mt-3 italic">Est. 2020 &middot; Anne Flinn Advertising &middot; Advertise Smarter with Us</p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-2xl font-bold text-[#003C59] mb-3">
            A simple process that takes a nagging frustration <span className="italic">(what should I post?)</span> off your list.
          </p>
          <p className="text-gray-500 text-lg mb-8 max-w-xl mx-auto">
            No learning curve, no extra staff, no guessing what to post.
            You approve everything. Anne handles the rest.
          </p>
          <button
            onClick={() => openContact('monthly')}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#01939E] hover:bg-[#017a83] text-white font-semibold rounded-lg shadow-lg shadow-[#01939E]/20 transition-all text-base cursor-pointer"
            data-testid="pricing-cta-btn"
          >
            Connect with Anne Today
          </button>
          <p className="text-xs text-gray-400 mt-3">No contracts. Cancel anytime.</p>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        tier={modalTier}
        industry={selectedIndustry}
      />

      {/* Footer */}
      <footer className="bg-[#003C59] text-white/50 py-8 mt-16">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="" className="h-8 w-auto opacity-50" />
          <div className="flex items-center gap-6 text-xs">
            <Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
          </div>
          <p className="text-xs">Anne Flinn Advertising</p>
        </div>
      </footer>
    </div>
  );
}
